// ─── Wire error codes ────────────────────────────────────────────────────────

/**
 * `code` on a 409 from POST /turn/attack: the dice count was legal when the
 * client built the request but the source stack has since thinned (a roll
 * resolved in between). Clients re-clamp and retry; it is not a client bug.
 * The body also carries `maxDice`, the count the server will accept now.
 */
export const DICE_COUNT_OUT_OF_RANGE = 'DICE_COUNT_OUT_OF_RANGE' as const

// ─── Auth ────────────────────────────────────────────────────────────────────

/** Versioned roster used when selecting bots for newly-created games. */
export const STABLE_BOT_ROSTER_VERSION = 2

/** Version of the decision strategy recorded in completed-game snapshots. */
export const STABLE_BOT_STRATEGY_VERSION = 1

/**
 * Stable bot identities are shared by the API and clients so the add-bot
 * picker cannot drift from the server's accepted profiles.
 */
export const STABLE_BOT_CATALOG = [
  {
    key: 'hannibal', username: 'bot-hannibal', displayName: 'Hannibal', personality: 'general',
    description: 'Balances expansion, defense, and attack opportunities.',
    backstory: [
      'Hannibal learned early that a battle is won long before the first die is cast. As a young commander of Carthage, he studied roads, weather, supply lines, and the habits of every rival who stood between him and home. His famous marches through impossible country were not acts of bravado; they were carefully prepared surprises designed to make the battlefield favor him before his opponents understood the position.',
      'That same instinct guides his play. Hannibal weighs useful expansion against the strength of his borders and the opportunities ahead. He is an adaptable commander, willing to build a position or press an opening as the board changes. His style is balance: no single kind of conquest defines his campaign.',
    ],
  },
  {
    key: 'sun-tzu', username: 'bot-sun-tzu', displayName: 'Sun Tzu', personality: 'opening',
    description: 'Favors early position, connected territory, and a secure base.',
    backstory: [
      'Sun Tzu built his reputation by treating preparation as a form of victory. He watched how armies moved, where commanders grew impatient, and which small advantages could become decisive once a campaign gathered momentum. To him, the opening was not a race for territory; it was the quiet work of arranging the board so every later choice became easier.',
      'On the map, Sun Tzu favors useful ground, connected positions, and a secure base for expansion. His preference is preparation over spectacle, building a campaign around the territory he can put to work. A strong opening is an advantage to develop, not a promise of victory.',
    ],
  },
  {
    key: 'boudica', username: 'bot-boudica', displayName: 'Boudica', personality: 'berserker',
    description: 'Favors the initiative, presses openings, and keeps opponents under pressure.',
    backstory: [
      'Boudica became a symbol of defiance when an empire answered a proud people with humiliation and force. She did not wait for perfect conditions or a polite invitation to fight. Her strength came from turning anger into momentum, gathering allies quickly, and striking hard enough that every victory created the next opportunity.',
      'Her strategy follows that same rhythm. Boudica favors initiative and looks to turn an opening into sustained pressure. She is more inclined to contest the board than settle into a quiet frontier, but aggression is a preference, not a promise to fight every battle. Her campaigns revolve around making progress before rivals can settle.',
    ],
  },
  {
    key: 'zenobia', username: 'bot-zenobia', displayName: 'Zenobia', personality: 'turtle',
    description: 'Favors defensible regions and strong borders, expanding from a secure base.',
    backstory: [
      'Zenobia ruled Palmyra from a crossroads where every road carried both opportunity and danger. She understood that a wealthy city survives only when its walls, allies, and reserves are stronger than the appetite of the powers around it. Her court rewarded patience, logistics, and the ability to make a defensive frontier feel impossible to breach.',
      'In a game, Zenobia favors territory she can hold and a frontier backed by useful reserves. She places more emphasis on protecting her position than chasing every opening. That does not make her passive: expansion and timely attacks still have a place in a campaign built around a durable base.',
    ],
  },
  {
    key: 'genghis', username: 'bot-genghis', displayName: 'Genghis', personality: 'general',
    description: 'Balances expansion and defense, with an emphasis on concentrated forces.',
    backstory: [
      'Genghis brought scattered forces together through organization, mobility, and a shared plan. He understood the value of gathering strength where it could matter most, keeping routes open, and adapting when a rival exposed a different frontier. Speed was useful because it gave him choices: secure valuable ground, reinforce a vulnerable position, or concentrate an army before the next campaign.',
      'On the board, Genghis is an adaptable generalist with an emphasis on bringing strength to useful fronts. Like Hannibal, he balances expansion with the position he leaves behind; his particular preference is for concentrated forces over scattered gains. He is not defined by a single target or a single route to victory.',
    ],
  },
  {
    key: 'tamerlane', username: 'bot-tamerlane', displayName: 'Tamerlane', personality: 'saboteur',
    description: 'Contests rival strongholds and looks for openings to disrupt their progress.',
    backstory: [
      'Tamerlane is a title earned by a strategist who made misdirection feel like an art. She studied the ambitions of powerful commanders and found that the strongest army was often the easiest to steer: threaten one frontier, force a reaction, then appear somewhere else with the real objective. Her campaigns were built around uncertainty, alliances of convenience, and never allowing a rival to settle comfortably into a winning shape.',
      'Her play is disruptive by design. Tamerlane gives particular attention to rival strongholds and contested ground, looking for ways to make an opponent\'s progress less comfortable. She favors pressure that interferes with a growing advantage, rather than treating every stretch of territory as equally valuable.',
    ],
  },
  {
    key: 'caesar', username: 'bot-caesar', displayName: 'Caesar', personality: 'risk-manager',
    description: 'Favors measured attacks, useful reserves, and careful expansion.',
    backstory: [
      'Caesar kept armies moving through careful accounting as much as through charisma. He knew the difference between a dramatic victory and a costly one, and he treated roads, reserves, and timing as weapons in their own right. His confidence came from making sure the next decision still had good options, even after the current battle was over.',
      'On the board, Caesar favors measured advances and a healthy reserve. He is inclined toward a position that leaves options for the next turn, rather than a dramatic conquest at any cost. His style is cautious, though neither caution nor confidence can make a battle certain.',
    ],
  },
  {
    key: 'artemisia', username: 'bot-artemisia', displayName: 'Artemisia', personality: 'endgame',
    description: 'Favors a durable position and looks to turn accumulated strength into progress.',
    backstory: [
      'Artemisia of Halicarnassus survived among rulers who mistook patience for hesitation. As a queen and admiral, she learned to conserve strength through long campaigns, read the moment when a grand power had overextended, and commit only when the final movement could decide the whole contest. Her authority came from knowing exactly when restraint had finished its job.',
      'Artemisia favors a durable base and the strength to make a useful opening count. Her style leans toward consolidation, while keeping an eye on opportunities to turn an advantage into a stronger position. Patience is part of her character, not a guarantee that the board will wait for her.',
    ],
  },
] as const

export type StableBotProfile = (typeof STABLE_BOT_CATALOG)[number]['key']

/** New selection and the gallery use this subset; historical identities stay intact. */
export const SELECTABLE_BOT_PROFILES = ['hannibal', 'boudica', 'zenobia', 'genghis', 'tamerlane'] as const satisfies readonly StableBotProfile[]
export function isSelectableBotProfile(profile: string): boolean {
  return (SELECTABLE_BOT_PROFILES as readonly string[]).includes(profile)
}
export const SELECTABLE_BOT_CATALOG = STABLE_BOT_CATALOG.filter((bot) => isSelectableBotProfile(bot.key))

export interface RegisterRequest {
  email: string
  password: string
  username: string
  displayName?: string
  /** Optional name shown on the user's public profile. */
  realName?: string
}

export interface LoginRequest {
  /**
   * Email or username — server figures out which by looking for an
   * "@" character. Username match is case-insensitive (so users
   * don't have to remember if they capitalised at register time).
   */
  identifier?: string
  /**
   * @deprecated Use `identifier`. Still accepted for backwards
   * compatibility with older clients.
   */
  email?: string
  password: string
}

export interface RefreshRequest {
  refreshToken: string
}

export interface TokenResponse {
  accessToken: string
  refreshToken: string
  expiresIn: string
  user: UserResponse
}

export interface ForgotPasswordRequest {
  email: string
}

export interface ResetPasswordRequest {
  token: string
  password: string
}

export interface RegisterResponse {
  message: string
  email: string
}

export interface ConfirmEmailRequest {
  token: string
}

export interface ConfirmEmailResponse {
  message: string
  user: UserResponse
}

export interface ResendConfirmationRequest {
  email: string
}

export interface ResendConfirmationResponse {
  message: string
}

/** 403 body returned when the user exists but has not confirmed their email. */
export interface UnverifiedEmailError {
  error: 'UNVERIFIED_EMAIL'
  message: string
  email: string
}

export interface RequestEmailChangeRequest {
  newEmail: string
}

export interface RequestEmailChangeResponse {
  message: string
}

export interface ConfirmEmailChangeRequest {
  token: string
}

export interface ConfirmEmailChangeResponse {
  message: string
  user: UserResponse
}

/** 400 body returned when the requested new email is already registered to another account. */
export interface EmailTakenError {
  statusCode: 400
  error: 'EMAIL_TAKEN'
  message: string
}

// ─── User ────────────────────────────────────────────────────────────────────

/**
 * Granular notification taxonomy. Each type can be toggled per channel; the two
 * `*Notifications` booleans below are the per-channel MASTERS (a channel off ⇒
 * nothing on that channel, regardless of the per-type toggles). A type that is
 * absent server-side is treated as ENABLED (everything is on by default).
 * Transactional email (confirm / reset / email-change) is never listed here —
 * it's always sent.
 */
/** Player name (displayName/username) length, enforced by the API and app inputs. */
export const PLAYER_NAME_MIN = 3
export const PLAYER_NAME_MAX = 20
/** Optional profile "Real Name" length, enforced by the API and app inputs. */
export const REAL_NAME_MAX = 20
/** Stable palette identities. Add slots; never reorder historical colors. */
export const PLAYER_COLOR_SLOTS = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const
export type PlayerColorSlot = (typeof PLAYER_COLOR_SLOTS)[number]
export const VACATION_MIN_DAYS = 1
export const VACATION_MAX_DAYS = 7
export interface UpdateVacationRequest { message: string }
export const VACATION_MESSAGE_MAX = 60
export interface VacationResponse { startedAt: string; endsAt: string; message: string | null }
export interface StartVacationRequest { days: number; message?: string }
export interface UserSearchResult { id: string; username: string; displayName: string; vacation?: VacationResponse | null }
export interface UserSearchResponse { results: UserSearchResult[] }

export const NOTIFICATION_TYPES = [
  'your-turn',
  'game-over',
  'player-eliminated',
  'player-booted',
  'direct-message',
  'game-invite',
  'inbox-message',
] as const
export type NotificationType = (typeof NOTIFICATION_TYPES)[number]
export type NotificationChannel = 'email' | 'push'
/** Per-type on/off map for one channel (every type present; true = enabled). */
export type NotificationTypeToggles = Record<NotificationType, boolean>
/** Resolved per-channel × per-type toggles (all keys present; defaults applied). */
export interface NotificationSettings {
  email: NotificationTypeToggles
  push: NotificationTypeToggles
}
/** Human labels for the settings UI (shared so client + server agree). */
export const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
  'your-turn': 'Your turn',
  'game-over': 'Game finished',
  'player-eliminated': 'Player eliminated',
  'player-booted': 'Player booted',
  'direct-message': 'Secret messages',
  'game-invite': 'Game invites',
  'inbox-message': 'Inbox messages',
}

/**
 * UI grouping for the settings screen. The wire protocol stays per-TYPE (see
 * NOTIFICATION_TYPES) — this only collapses related types under one switch so
 * the screen isn't a wall of toggles. A group switch flips every `type` it
 * lists at once; a group is "on" iff all its members are on.
 *
 * INVARIANT: every NotificationType appears in exactly ONE group (no orphans,
 * no duplicates) so nothing is silently unreachable from the UI. Locked by a
 * test — see apps/mobile/tests/notificationGroups.test.ts.
 */
export interface NotificationGroup {
  key: string
  label: string
  types: readonly NotificationType[]
}
export const NOTIFICATION_GROUPS: readonly NotificationGroup[] = [
  { key: 'your-turn', label: 'Your turn', types: ['your-turn'] },
  { key: 'game-updates', label: 'Game updates', types: ['game-over', 'player-eliminated', 'player-booted'] },
  { key: 'game-invites', label: 'Game invites', types: ['game-invite'] },
  { key: 'messages', label: 'Secret messages', types: ['direct-message'] },
  { key: 'inbox', label: 'Inbox messages', types: ['inbox-message'] },
]

export interface UserResponse {
  id: string
  email: string
  username: string
  displayName: string
  /** Optional self-reported identity (real/first name or abbreviated email)
   *  shown on the public profile so friends can tell who is who. */
  realName: string | null
  /** Per-channel master toggles (a channel off ⇒ nothing sent on it). */
  emailNotifications: boolean
  pushNotifications: boolean
  /** Per-channel × per-type toggles, fully resolved (all types present). */
  notificationSettings: NotificationSettings
  /** Whether the one-time welcome/tutorial prompt has been shown. */
  tutorialWelcomeSeen: boolean
  /** First recorded milestones. Older API deployments may omit these fields. */
  tutorialStartedAt?: string | null
  tutorialCompletedAt?: string | null
  practiceGameCompletedAt?: string | null
  /** Absent on older servers; null when the player is not on vacation. */
  vacation?: VacationResponse | null
  /** Default player color (palette slot 1..9) picked automatically on
   *  create/join when free in that game; null = no preference. */
  preferredColorSlot: number | null
  createdAt: string
}

export type TutorialMilestone = 'started' | 'completed'
export interface RecordTutorialMilestoneRequest { milestone: TutorialMilestone }
export interface RecordTutorialMilestoneResponse { recordedAt: string }

export interface UpdateUserRequest {
  displayName?: string
  /** Set/replace the profile real name; null or '' clears it. */
  realName?: string | null
  emailNotifications?: boolean
  pushNotifications?: boolean
  /** Set the default player color (1..9); null clears it. */
  preferredColorSlot?: number | null
  /** Partial per-type toggle updates; merged server-side (omit types to leave
   *  them unchanged). */
  notificationSettings?: {
    email?: Partial<NotificationTypeToggles>
    push?: Partial<NotificationTypeToggles>
  }
}

export interface BlockablePlayer {
  /** Bot identities can be reported, but cannot be blocked. */
  canBlock?: boolean
  id: string
  username: string
  displayName: string
}

export interface BlockedUsersResponse {
  users: BlockablePlayer[]
}

export interface RegisterPushTokenRequest {
  token: string
}

// ─── Game ─────────────────────────────────────────────────────────────────────

export type GameStatus = 'LOBBY' | 'STARTING' | 'ACTIVE' | 'PAUSED' | 'COMPLETE' | 'ABANDONED'
export type GameMode = 'PASS_AND_PLAY' | 'LIVE' | 'SOLO'
export type PlayerRole = 'ADMIN' | 'MEMBER'

export const BOOT_TIMER_OPTIONS = [{ seconds: 3600, label: '1 hour' }, { seconds: 43200, label: '12 hours' }, { seconds: 86400, label: '1 day' }, { seconds: 259200, label: '3 days' }, { seconds: 432000, label: '5 days' }, { seconds: 604800, label: '1 week' }, { seconds: 1209600, label: '2 weeks' }] as const
export const DEFAULT_BOOT_AFTER_SECONDS = 432000
export const bootTimerAdjective = (seconds: number) => {
  const label = BOOT_TIMER_OPTIONS.find(option => option.seconds === seconds)?.label ?? '5 days'
  return label.replace(' ', '-').replace(/s$/, '')
}

export interface CreateGameRequest {
  name?: string
  isPublic?: boolean
  isRanked?: boolean
  mapId?: string
  config?: Partial<GameConfig>
  mode?: GameMode
  minPlayers?: number
  maxPlayers?: number
  /** When true (default), the game auto-starts once every seat is filled by a
   *  real player. When false, the host starts it manually. */
  autoStart?: boolean
  bootAfterSeconds?: number
}

/** PATCH /api/v1/games/:id/settings — host-only lobby edits to the Game-level
 *  settings (maxPlayers + autoStart), distinct from GameConfig (PATCH /setup). */
export interface UpdateGameSettingsRequest {
  isRanked?: boolean
  /** New max players (2..6). Cannot be lowered below the highest occupied or
   *  reserved seat. */
  maxPlayers?: number
  autoStart?: boolean
  bootAfterSeconds?: number
  /** Public games are browsable/joinable by anyone; private games require a
   *  direct invite or the invite code. */
  isPublic?: boolean
  /**
   * Team format (docs/team-games-plan.md); null switches back to
   * free-for-all. Send it with `maxPlayers` to change size and count in one
   * step. The count must be a valid format for the map (`validateTeamFormat`):
   * an omitted `maxPlayers` keeps the current count when valid, else snaps to
   * the closest (`snapTeamPlayers`). A team lobby keeps minPlayers =
   * maxPlayers and auto-start on.
   */
  teams?: TeamsConfig | null
}

/** Public lobbies to join. Joined games and pending invitations stay in the
 * viewer's home list and are excluded from this cursor-paginated browse. */
export interface LobbyGamesResponse {
  games: GameResponse[]
  nextCursor: string | null
  /** Total public LOBBY games with a free seat that the viewer has neither
   * joined nor been invited to, independent of pagination.
   * Missing on older servers. Private invitations do not contribute. */
  publicOpenGameCount?: number
}

export interface JoinGameRequest {
  inviteCode: string
  /** Optional desired color (1..9). Omit to auto-assign the lowest available. */
  color?: number
}

/** GET /api/v1/games/by-code/:code — unauthenticated game preview */
export interface PreviewGameResponse {
  id: string
  name: string | null
  /**
   * 4-digit numeric PIN. Always present here — the preview endpoint
   * only resolves LOBBY games (started games have null codes and
   * won't match the lookup).
   */
  inviteCode: string
  status: GameStatus
  isPublic: boolean
  mapId: string | null
  setupConfig: Partial<GameConfig> | null
  minPlayers: number
  maxPlayers: number
  host: {
    displayName: string
    username: string
  }
  players: Array<{
    slot: number
    displayName: string
    isBot: boolean
    /** Chosen palette slot (null for legacy rows: render the seat slot). Optional for older servers. */
    colorSlot?: number | null
    /** True for the host's seat. Optional for older servers. */
    isHost?: boolean
  }>
  /** Slot numbers (1..maxPlayers) not yet claimed by any player. */
  availableSlots: number[]
}

/** POST /api/v1/games/:id/join */
/** POST /games/:id/add-bot (host only, lobby only). */
export interface AddBotRequest {
  /** A stable bot key (STABLE_BOT_ROSTER). Omit for the next available bot. */
  botProfile?: string
  displayName?: string
  /**
   * The seat the bot takes (1..maxPlayers), so a Pick-seats team lobby can fill
   * a chosen team. Omit for the lowest open seat. 409 SLOT_TAKEN when the seat
   * is taken or saved for an invited player.
   */
  slot?: number
}

export interface JoinGameByIdRequest {
  /**
   * Optional desired seat (1..maxPlayers). Omit to auto-assign lowest available.
   * The seat is normally auto-assigned; kept for reserved-seat / legacy callers.
   */
  slot?: number
  /** Optional desired color (1..9). Omit to auto-assign the lowest available. */
  color?: number
}

/** PATCH /api/v1/games/:id/me/slot */
export interface UpdateMySlotRequest {
  slot: number
}

/** PATCH /api/v1/games/:id/me/slot — response */
export interface UpdateMySlotResponse {
  game: GameResponse
}

/** PATCH /api/v1/games/:id/me/color — change the caller's color in a LOBBY game */
export interface UpdateMyColorRequest {
  /** Desired color palette slot (1..6). Must be free within the game. */
  color: number
}

/** PATCH /api/v1/games/:id/me/color — response */
export interface UpdateMyColorResponse {
  game: GameResponse
}

/** 409 — the requested slot is already occupied by another player. */
export interface SlotTakenError {
  statusCode: 409
  error: 'SLOT_TAKEN'
  message: string
}

/** 409 — the requested color is already used by another player in the game. */
export interface ColorTakenError {
  statusCode: 409
  error: 'COLOR_TAKEN'
  message: string
}

/** 400 — the game has already started; slot changes are not permitted. */
export interface GameStartedError {
  statusCode: 400
  error: 'GAME_STARTED'
  message: string
}

/** POST /api/v1/games/:id/leave — success response. */
export interface LeaveGameResponse {
  message: string
}

/** POST /api/v1/games/:id/redeem-code — request body. */
export interface RedeemInviteCodeRequest {
  /** 4-digit numeric PIN matching game.inviteCode. */
  code: string
}

/**
 * POST /api/v1/games/:id/redeem-code — 200 success response.
 *
 * A pending GameInvitation has been created for the viewer.
 * Returns the full game so the client can drop straight into
 * the unified /games/[id] LOBBY view without a follow-up GET.
 */
export type RedeemInviteCodeResponse = GameResponse

/** 404 — the caller is not a player in this game. */
export interface NotInGameError {
  statusCode: 404
  error: 'NOT_IN_GAME'
  message: string
}

/** 403 — the host cannot leave; they must cancel the game instead. */
export interface HostCannotLeaveError {
  statusCode: 403
  error: 'HOST_CANNOT_LEAVE'
  message: string
}

/**
 * A pending or accepted invitation to a game.
 * Included in GameResponse.invitations for LOBBY games.
 */
export interface GameInvitationResponse {
  id: string
  /** 'declined' = the invited user turned it down (self-serve); the seat it
   *  reserved opens immediately. 'revoked' = the host withdrew it. */
  status: 'pending' | 'accepted' | 'revoked' | 'declined'
  sentAt: string
  respondedAt: string | null
  /** The seat this invite reserves while pending — only the invited user may
   *  fill it. Null for non-reserving invites (legacy + invite-code redemptions). */
  slot: number | null
  user: Pick<UserResponse, 'id' | 'username' | 'displayName'>
  /**
   * True if the invited user has a working notification channel (push or
   * email). When false the host's "pending invites" row shows a "share the
   * code" hint — they won't be auto-notified.
   */
  notifiable: boolean
}

export interface GameResultSnapshotPlacement {
  slot: number
  playerIndex: number
  userId: string
  username: string
  displayName: string
  isBot: boolean
  botProfile: string | null
  personality: string | null
  strategyVersion: number
  score: number | null
  /** 1 = best. In a team game every member shares their team's placement. */
  placement: number
  /** Team games only: the seat's team. Absent in free-for-all. */
  team?: TeamId
}

/** Immutable terminal result metadata. The final board is returned in `state`. */
export interface GameResultSnapshotResponse {
  schemaVersion: number
  /** The winner; in a team game the winning team's first member in turn order. */
  winnerSlot: number | null
  /**
   * Team games only (absent in free-for-all): the winning team (null on a
   * tie) and every winning seat, members eliminated earlier included.
   */
  winnerTeam?: TeamId | null
  winnerSlots?: number[]
  endReason: 'last-standing' | 'stalled' | 'resign' | 'admin-complete'
  completedAt: string
  placements: GameResultSnapshotPlacement[]
  /** Missing/null on snapshots created before dice luck was recorded. */
  diceLuck?: import('./diceLuck.js').DiceLuckSnapshot | null
}

export interface GameResponse {
  id: string
  adminId: string
  /**
   * 4-digit numeric PIN. Present while the game is in LOBBY status;
   * `null` once the game transitions to ACTIVE / COMPLETE / ABANDONED
   * (the PIN is recycled back into the 10 000-slot pool).
   */
  inviteCode: string | null
  name: string | null
  status: GameStatus
  mode: GameMode
  minPlayers: number
  maxPlayers: number
  /** When true, the game auto-starts once every seat is filled by a real
   *  player; when false, the host starts it manually. */
  autoStart: boolean
  bootAfterSeconds: number
  bootableAt: string | null
  /** Version of the bot roster used by this game (0 means legacy behavior). */
  botRosterVersion: number
  currentTurn: number
  currentPlayerIndex: number
  state: Record<string, unknown> | null
  /** Present for COMPLETE games after a terminal result snapshot is available. */
  resultSnapshot?: GameResultSnapshotResponse | null
  /**
   * The GET /games list only (which carries no state or snapshot): a finished
   * team game's winning team (null on a tie) and winning seats, from its
   * result snapshot. Absent for free-for-all and unfinished games.
   */
  winnerTeam?: TeamId | null
  winnerSlots?: number[]
  /** Whether this game is listed in the public lobby browser. */
  isPublic: boolean
  /** Missing on older servers means ranked. Immutable after the game starts. */
  isRanked?: boolean
  /** Map selected for this game (pre-filled at create, used at start). */
  mapId: string | null
  /** Setup configuration pre-filled at create time. */
  setupConfig: Partial<GameConfig> | null
  players: GamePlayerResponse[]
  /**
   * Pending and accepted invitations. Populated for LOBBY games; empty array
   * for games in any other status. The host uses this to render a
   * pending-invites list in the waiting room.
   */
  invitations: GameInvitationResponse[]
  /**
   * Viewer-relative tag set by the /games (my-games) endpoint only.
   * `'pending'` means the viewer has a pending invitation to this
   * game and has not yet joined as a player — the mobile tile renders
   * a "Pending Invite!" badge in this case. `null` otherwise, including
   * for endpoints that don't pass the viewer to formatGame.
   */
  viewerInvitationStatus: 'pending' | null
  startedAt: string | null
  /** When the current turn began; used to discard stale foreground alerts. */
  turnStartedAt?: string | null
  completedAt: string | null
  createdAt: string
  /** Last row write of any kind, including background jobs such as turn
   *  reminders. The home "Current" tab orders LOBBY games by this; started
   *  games order by `turnStartedAt` instead (see mobile `lib/lobbyTabs.ts`). */
  updatedAt: string
  /**
   * Id of the most recent PUBLIC chat message in this game, or `null` if the
   * game has no chat yet. Set by the /games (my-games) list endpoint only
   * (0 elsewhere-relevant callers leave it `null`). The client compares it
   * against its locally-stored per-game "last seen chat id" to show an unread
   * indicator in the game list WITHOUT loading the full message thread.
   */
  lastChatMessageId: string | null
  /**
   * Count of SECRET messages to the viewer in this game that are still
   * unread: 1:1 messages with `readAt = null`, plus, in a team game, team chat
   * messages from teammates since the viewer last read it. Server-authoritative; set by the
   * /games (my-games) endpoint (which knows the viewer). `0` for callers that
   * don't pass a viewer. Drives the game-list unread indicator alongside
   * `lastChatMessageId`.
   */
  unreadSecretCount: number
}

export interface GamePlayerResponse {
  vacation?: VacationResponse | null
  id: string
  userId: string
  user: Pick<UserResponse, 'id' | 'username' | 'displayName'>
  /** 0-indexed turn-order index. May be reassigned if players are eliminated. */
  playerIndex: number
  /**
   * 1-indexed seat / turn identity (1..maxPlayers). Stable per game and used by
   * the engine as the player's id. NOT the render color — see `colorSlot`.
   */
  slot: number
  /**
   * 1-indexed color palette slot (maps to PLAYER_COLORS in @warcarp/ui), chosen
   * by the player and unique per game. Decoupled from `slot`. Null only for
   * legacy rows — clients fall back to `slot` when null.
   */
  colorSlot: number | null
  displayName: string
  color: string | null
  isReady: boolean
  /**
   * True for AI-controlled players added via POST /games/:id/add-bot.
   * Server-side bot driver auto-plays whenever the current player is a bot.
   */
  isBot: boolean
  /**
   * Stable bot catalog key for new-roster games. Null for humans and legacy
   * bot players. This is exposed so result snapshots and future bot stats can
   * identify the bot without relying on a generated per-game User row.
   */
  botProfile: string | null
  /**
   * For bot players only: the personality assigned to this bot for the lifetime
   * of the game. Null for human players or legacy games (before Phase 2).
   * Current personalities include general, turtle, berserker, saboteur,
   * opening, risk-manager, eliminator, and endgame.
   */
  botPersonality: string | null
  /**
   * Set when an AI took this seat over from a human who stopped taking turns: the
   * booted player's user id and when it happened. Optional for older servers.
   */
  takenOverFromUserId?: string | null
  takenOverAt?: string | null
  /** Why the seat was taken over: 'turn-timeout' or 'abandoned'. */
  takenOverReason?: 'turn-timeout' | 'abandoned' | null
  /** Completed turns this seat has played in this game. Optional for older servers. */
  turnCount?: number
  /** Mean turn length in this game, in seconds; null before the first completed turn. */
  averageTurnSeconds?: number | null
  score: number | null
  rank: number | null
  joinedAt: string
}

// ─── Cards ───────────────────────────────────────────────────────────────────

export type CardType = 'infantry' | 'cavalry' | 'artillery' | 'wild'

export interface Card {
  /** Stable per card across the lifetime of the deck — e.g. `card-${index}`. */
  id: string
  type: CardType
  /**
   * Present on territory cards (infantry/cavalry/artillery). Used for set
   * validation only in v1; no territory-match bonus is applied on turn-in.
   */
  territoryId?: string
}

/** Every card set bonus schedule, in the order the setup page lists them. */
export const CARD_BONUS_SCHEDULES = ['cycle', 'incremental', 'fixed', 'climb', 'surge'] as const
export type CardBonusSchedule = (typeof CARD_BONUS_SCHEDULES)[number]

/**
 * How each schedule reads in the app: its name, the sequence of bonuses, and
 * a one-line description. One definition for setup, rules views and summaries.
 */
export const CARD_BONUS_SCHEDULE_INFO: Record<CardBonusSchedule, { label: string; sequence: string; detail: string }> = {
  cycle: { label: 'Cycle', sequence: '4, 6, 8, 10, 4, 6…', detail: 'Bonus troops 4, 6, 8, 10, then back to 4' },
  incremental: { label: 'Incremental', sequence: '4, 5, 6, 7, 8, 9…', detail: 'Bonus troops 4, 5, 6, 7… one more each time' },
  fixed: { label: 'Fixed', sequence: '5, 5, 5, 5…', detail: 'Bonus troops 5 every time' },
  climb: { label: 'Climb', sequence: '3, 6, 9, 12, 15…', detail: 'Bonus troops 3, 6, 9, 12… three more each time' },
  surge: { label: 'Surge', sequence: '5, 10, 15, 20, 25…', detail: 'Bonus troops 5, 10, 15, 20, 25… five more each time' },
}

/** The info for a schedule, falling back to Cycle (the default) for anything unknown. */
export function cardBonusScheduleInfo(schedule: unknown): { label: string; sequence: string; detail: string } {
  return CARD_BONUS_SCHEDULE_INFO[schedule as CardBonusSchedule] ?? CARD_BONUS_SCHEDULE_INFO.cycle
}

/** POST /api/v1/games/:id/turn/turn-in-cards */
export interface TurnInCardsRequest {
  /** Exactly 3 card IDs from the caller's hand, forming a valid set. */
  cardIds: string[]
}

// ─── Game Config ─────────────────────────────────────────────────────────────

/** Per-game game configuration knobs. */
export interface GameConfig {
  /** Total troops per player at game start (includes 1-per-territory auto-placement). */
  startingTroops: number
  /** Maximum attack actions per turn. null = unlimited. */
  maxAttacksPerTurn: number | null
  /** Maximum fortify actions per turn. null = unlimited. */
  maxFortifiesPerTurn: number | null
  /**
   * Maximum number of complete rounds before force-termination. Defaults to 500.
   * null disables the cap.
   */
  maxTurns: number | null
  /**
   * When true, skip the claim phase: territories are distributed randomly
   * and starting armies seeded automatically. When false (default), the
   * claim phase runs and each player picks their territories manually.
   */
  autoAssignTerritories: boolean
  /**
   * Bonus armies granted to the attacker each time their attack eliminates
   * another player. Default 5. The attacker also inherits ALL of the
   * eliminated player's cards.
   *
   * The server always fills this from DEFAULT_CONFIG before persisting — it
   * is always present on both wire responses and engine state. Clients that
   * send a partial setup config may omit it (the server fills it in).
   */
  eliminatePlayerBonus: number
  /**
   * How the per-set turn-in bonus is calculated.
   *   - 'cycle'       — bonuses cycle [4, 6, 8, 10] indexed by cardTurnIns mod 4.
   *   - 'incremental' — bonus = 4 + cardTurnIns (escalating, no plateau).
   *   - 'fixed'       — always 5.
   *   - 'climb'       — 3, 6, 9, 12… (3 × (cardTurnIns + 1)).
   *   - 'surge'       — 5, 10, 15, 20… (5 × (cardTurnIns + 1)).
   *
   * The server always fills this from DEFAULT_CONFIG before persisting — it
   * is always present on both wire responses and engine state. Clients that
   * send a partial setup config may omit it (the server fills it in).
   */
  cardBonusSchedule: CardBonusSchedule
  /**
   * Fog of war (docs/fog-of-war/README.md). Absent in every pre-fog game and
   * in default lobbies, and absent means `'none'`; read it through
   * `fogOfWarOf(config)`. Not part of DEFAULT_GAME_CONFIG on purpose, so
   * newcomer lobbies (matched on an exact default setupConfig) are unchanged.
   *   - `'light'`: owners visible everywhere, troops only within your vision;
   *   - `'heavy'`: nothing outside your vision.
   */
  fogOfWar?: FogOfWar
  /**
   * Team play (docs/team-games-plan.md). Absent in every free-for-all game
   * and absent means free-for-all; never part of DEFAULT_GAME_CONFIG, so
   * newcomer lobbies (matched on an exact default setupConfig) are unchanged.
   *   - `size`: players per team. The team count is `maxPlayers / size`.
   *   - `assignment`: `'random'` draws the teams at start; `'manual'` groups
   *     the seats contiguously (see `teamForSlot`).
   * Valid formats come from `teamFormats` / `validateTeamFormat` (teams.ts).
   */
  teams?: TeamsConfig
}

/** Fog of war mode for a game. See {@link GameConfig.fogOfWar}. */
export type FogOfWar = 'none' | 'light' | 'heavy'

/** POST /api/v1/games/:id/start */
export interface StartGameRequest {
  /**
   * @deprecated The map is locked at creation time (game.mapId). This field is
   * accepted for backwards compatibility but is ignored by the server — the
   * server always uses the mapId stored on the game record. Callers should omit
   * this field; it will be removed in a future version.
   */
  mapId?: string
  /** Optional game configuration; missing fields fall back to server defaults. */
  config?: Partial<GameConfig>
}

/** POST /api/v1/games/:id/turn/claim
 *
 * Place 1 starting army on a territory during the claim phase.
 *  - If the territory is unowned, this claims it for the caller.
 *  - If all territories are already claimed, the territory must be one the caller owns.
 *  - The caller's `armiesToPlace` is decremented by 1.
 *  - Turn advances to the next player whose `armiesToPlace > 0`.
 *  - When everyone's `armiesToPlace == 0`, the phase transitions to `reinforce` (turnIndex=0).
 */
export interface ClaimRequest {
  territoryId: string
}

/** One territory placement in a reinforce request. */
export interface ReinforcePlacement {
  territoryId: string
  armies: number
}

/** POST /api/v1/games/:id/turn/reinforce */
export interface ReinforceRequest {
  placements: ReinforcePlacement[]
}

/**
 * POST /api/v1/games/:id/turn/post-elim-place
 *
 * Interrupt placement during the 'post-elim-placement' phase. The attacker
 * spends their just-credited elimination bonus armies before resuming attacks.
 * Budget = state.bonusArmies[playerId] only (NOT the standard base+region
 * reinforcement count). Phase returns to 'attack' after success.
 */
export interface PostElimPlaceRequest {
  placements: ReinforcePlacement[]
}

/** POST /api/v1/games/:id/turn/attack */
export interface AttackRequest {
  fromTerritoryId: string
  toTerritoryId: string
  /**
   * Number of attacker dice to roll. Must satisfy
   * 1 ≤ diceCount ≤ min(3, source.armies - 1).
   *
   * In blitz mode this is the *initial* dice count; the engine re-clamps
   * to min(3, source.armies-1) before each iteration as armies are lost.
   */
  diceCount: number
  /**
   * Blitz mode: keep attacking the same target until the source would
   * risk dropping to or below this floor on the next roll, the target is
   * conquered, or the per-turn attack cap is reached. The engine stops
   * iterating when `source.armies - currentDiceCount < untilTroops`, so
   * the worst-case outcome of the *last* roll still leaves at least
   * `untilTroops` armies on the source. Omit (or 0) for a single attack.
   */
  untilTroops?: number
}

/** Dice roll details — attacker and defender dice values (sorted descending). */
export interface DiceRollResult {
  attacker: number[]
  defender: number[]
}

/**
 * Alias for DiceRollResult used in GameEvent and engine internals.
 * Both names refer to the same shape; prefer DiceRollResult on the wire
 * and DiceRoll in engine-internal contexts.
 */
export type DiceRoll = DiceRollResult

/** POST /api/v1/games/:id/turn/attack — response.
 *
 * For a single attack (untilTroops omitted), `rolls.length === 1` and the
 * top-level fields refer to that one engagement.
 *
 * For a blitz attack (untilTroops set), `rolls` contains every roll the
 * engine ran in sequence; `attackerLost`/`defenderLost` are cumulative
 * across the batch, `roll` is the final iteration, and `conquered` is
 * true iff the defender hit 0 armies at some point.
 */
export interface AttackResponse {
  /** Total attacker armies lost across all iterations in this request. */
  attackerLost: number
  /** Total defender armies lost across all iterations in this request. */
  defenderLost: number
  /** True if the defender was reduced to 0 armies during this request. */
  conquered: boolean
  /** The final iteration's dice roll. For single attacks, this is the only roll. */
  roll: DiceRollResult
  /** Every roll in iteration order. Always non-empty. */
  rolls: DiceRollResult[]
  /** Number of attack iterations the engine ran (1 for single-attack). */
  iterations: number
  /** Updated game state after the attack. */
  game: GameResponse
}

/** POST /api/v1/games/:id/turn/fortify */
export interface FortifyRequest {
  fromTerritoryId: string
  toTerritoryId: string
  armies: number
}

/** POST /api/v1/games/:id/turn/transfer — post-conquest army transfer.
 *
 * Server reads the source/target pair from state.lastConquest. The client
 * only declares how many additional armies to move from the conquering
 * source into the newly-conquered territory. Valid only in the attack
 * phase, and only while a conquest is pending a transfer.
 */
export interface TransferRequest {
  armies: number
}

/**
 * POST /api/v1/games/:id/turn/return-to-attack
 *
 * Reverts the current player's phase from 'fortify' back to 'attack'.
 * Only valid when fortifiesThisTurn === 0 (no fortify has been committed yet
 * this turn). Returns 409 if a fortify has already been made.
 *
 * No request body required. Response is the updated GameResponse.
 */
export type ReturnToAttackResponse = GameResponse

// ─── Shared game shapes ───────────────────────────────────────────────────────
// Single source of truth for types shared between the engine (apps/api/src/engine)
// and the mobile client. The engine imports these from @warcarp/api-types — there
// is no duplication or hand-transcription.

import type { BorderDice } from './gameLogic.js'
import type { RuleOverlay } from './ruleOverlay.js'

export type PlayerId = string

/** A team in a team game. Teams are named by letter: "Team A" … "Team D". */
export type TeamId = 'A' | 'B' | 'C' | 'D'
/** Players per team. */
export type TeamSize = 2 | 3 | 4
/** `GameConfig.teams`: the team format a lobby was set up with. A type alias, not an interface, so it stays assignable to Prisma JSON. */
export type TeamsConfig = { size: TeamSize; assignment: 'random' | 'manual' }
/** `GameState.teams`: every seat's team, keyed by player id (slot string). */
export type TeamMembership = Record<PlayerId, TeamId>
export type TerritoryId = string

export interface Territory {
  id: TerritoryId
  name: string
  ownerId: PlayerId
  armies: number
  /**
   * Client-side only, fog-of-war games: hydrated from a fogged wire row
   * (docs/fog-of-war/README.md). `armies` is then 0 and meaningless, and
   * `ownerId` is '' in heavy fog. Renderers and roster maths must check this
   * rather than read the troop count. Never set by the engine.
   */
  fogged?: boolean
  /**
   * Client-side only: fogged AND the owner withheld (heavy fog, the wire row
   * has no ownerId). Light fog sends the owner, '' for neutral, so a neutral
   * there is not hidden: only heavy fog draws the striped hatch.
   */
  ownerHidden?: boolean
  /** Two-way borders. Symmetric: if a lists b, b lists a. */
  adjacencies: TerritoryId[]
  /**
   * ONE-WAY borders: territories this one borders in the outgoing direction
   * only. A real border for both attack AND fortify — the far side simply has
   * no matching edge back, so it can neither attack nor fortify into this one.
   * Capture the Flag's watchtowers hold their compound this way. A territory may
   * have both kinds at once. Use `borderReach()` / `hasBorderTo()` from
   * './gameLogic.js' rather than reading this directly, so client and server
   * agree on what is reachable.
   */
  oneWayBorders?: TerritoryId[]
  /**
   * Per-border die size, keyed by the neighbour on the far side: the number of
   * SIDES this territory's dice have when it fights across that border. A bare
   * number covers both attacking and defending; `{ attack, defend }` sets them
   * apart. Absent — and any neighbour or mode not listed — means the standard
   * six.
   *
   * Per SIDE and per MODE: a→b may be d8 while b→a stays d6, so Capture the
   * Flag's towers ATTACK at d8 without their neighbours rolling d8 back, and its
   * forts DEFEND at d7 without attacking at d7. Read it through
   * `attackDiceSides()` / `defendDiceSides()` in './gameLogic.js'.
   */
  borderDice?: Record<TerritoryId, BorderDice>
  /**
   * Hard cap on how many armies this territory may hold. Absent means
   * UNLIMITED, which is the default for every territory on every map.
   *
   * Enforced on every inflow — reinforcement placement, the post-elimination
   * interrupt, fortify, and the armies that move in on a conquest (clamped, the
   * surplus stays home). Combat losses and existing over-cap stacks are left
   * alone: a cap limits what you can ADD, it never deletes armies.
   */
  maxArmies?: number
  regionId?: string
  /** Centroid for label/troop-count placement. */
  position: { x: number; y: number }
  /**
   * Manual troop-badge anchor set via the map editor's drag-to-place; when
   * present the renderer uses it verbatim, bypassing the PoI/alignment
   * heuristic. Absent for auto-placed territories.
   */
  badgeAnchor?: { x: number; y: number }
  /**
   * Radius (viewBox units) of the largest circle round the badge anchor that
   * stays inside the territory, baked by the map's build alongside
   * `badgeSize`. Informational: MapView draws from `badgeSize`.
   */
  badgeRadius?: number
  /**
   * On a map with `MapDef.fitTroopBadges`: `'xsmall'` where a three-digit count
   * at the small size would not fit round the badge anchor (towers and other
   * small spaces), baked by the map's build. Absent: the small size. Fixed per
   * territory, so a count never changes size as it grows. Ignored elsewhere.
   */
  badgeSize?: 'xsmall'
  /**
   * On a map with `MapDef.fitTroopBadges`: the count steps down a little at two
   * digits and a little more at three (`tieredBadgeFontSize`), for compact
   * symbols such as Normulli Sector's towers and crystals. Baked by the build.
   */
  badgeShrinksWithDigits?: boolean
  /** SVG path `d` for the territory polygon. Optional; renderer falls back to a circle if absent. */
  shape?: string
}

/**
 * A map that dictates its own opening position instead of letting the lobby
 * choose one.
 *
 * When a map carries this, the manual/auto placement choice and the
 * starting-troops setting do not apply — the board decides. `playerCount` is
 * the maximum number of supported players; omit `minPlayers` for an exact
 * player count, or set it when the board supports a player-count range.
 *
 * Capture the Flag uses exactly 4 players, each dealt one of four home bases at
 * random. Hexcore uses 2–6 players, each dealt one of six home hexes; unused
 * homes remain neutral with their board-defined garrison.
 */
export interface FixedSetup {
  /** The map's maximum supported player count (or exact count if minPlayers is omitted). */
  playerCount: number
  /** Minimum supported player count for variable-size fixed openings. Defaults to playerCount. */
  minPlayers?: number
  /**
   * One area per maximum player, dealt at random — `areas.length` must equal
   * `playerCount`. When fewer than the maximum number of players join, only
   * that many areas are dealt; unused areas fall through to the neutral rules.
   * Each maps territoryId → the armies that territory starts with for the
   * player who draws the area. Territories the area does not list are NOT
   * owned by that player.
   */
  areas: Array<{ id: string; armies: Record<TerritoryId, number> }>
  /**
   * How areas are dealt when fewer than `playerCount` players join. Absent:
   * any `n` areas at random. `'ring'`: `areas` are listed in order around a
   * ring (each next to the one before it, the last next to the first), and the
   * deal spreads players as evenly as the count allows, so no player gets more
   * empty seats beside them than another (engine/fairSeating.ts). Royal Rumble.
   */
  seating?: 'ring'
  /**
   * Ring boards: the only seatings allowed for a player count, as indices into
   * `areas` in ring order (each plan ascending). Overrides the fairest-spread
   * search for that count, e.g. Normulli Sector's four players on the four
   * corner bases. Team games still keep teammates apart within these plans.
   */
  seatPlans?: Partial<Record<number, number[][]>>
  /** Armies on every territory no player owns. */
  neutralArmies: number
  /** Per-territory overrides of `neutralArmies`. */
  neutralOverrides?: Record<TerritoryId, number>
  /**
   * Game config the board PINS. Any subset of GameConfig: whatever is listed is
   * forced at create time and shown read-only in setup, and whatever is not
   * stays configurable. A board that dictates its opening may also dictate the
   * rules that opening depends on.
   */
  config?: Partial<GameConfig>
}

export interface Region {
  id: string
  name: string
  territoryIds: TerritoryId[]
  bonus: number
  /** SVG path `d` for the continent outline. Optional. */
  shape?: string
  /**
   * Optional perimeter colour override (hex like '#d83a0e'). When set,
   * MapView pins this region to the given colour and assigns the rest
   * of the regions around it. Source: the map-editor's
   * `regionColorOverrides` sidecar field, applied via
   * tools/map-editor/apply-overrides.mjs.
   */
  color?: string
  /**
   * Optional manual anchor (viewBox coords) for the region-bonus badge
   * that appears when the player toggles the region overlay. When unset
   * MapView falls back to an auto-computed centroid via
   * `regionBadgeAnchor`. Source: the map-editor's
   * `regionBonusAnchors` sidecar field.
   */
  bonusAnchor?: { x: number; y: number }
}

/**
 * Sea-route geometry precomputed by the map bundle pipeline. The field is
 * optional for backwards compatibility with older API responses; clients
 * fall back to computing it when it is absent or stale.
 */
/** One row of a map key: a rule symbol and what it means. */
export interface MapLegendItem {
  /** `route`: a crossing line; `arrow`: a one-way crossing; `chevron`: a border chevron. */
  symbol: 'route' | 'arrow' | 'chevron'
  /** Which shared symbol colour to draw it in (defaults: chevron = advantage, otherwise route). */
  kind?: 'route' | 'disadvantage' | 'advantage'
  label: string
}

/** Optional map key: always open at a corner of the drawn map, listing its items. */
export interface MapLegend {
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  /**
   * Optional: the key's vertical centre, in map (viewBox) units, instead of the
   * corner's top or bottom edge. The side edge still lines up with the map.
   * Hexcore centres its key between two right-hand home hexes so it never
   * overlaps one on a narrow phone.
   */
  centerY?: number
  items: MapLegendItem[]
}

export interface MapSeaRoute {
  aId: string
  bId: string
  from: { x: number; y: number }
  to: { x: number; y: number }
  /** One-way crossing (Territory.oneWayBorders): `aId` attacks `bId` only; drawn with an arrowhead at `to`. */
  oneWay?: boolean
}

/** Lightweight map-list entry; presentation metadata also covers maps added after a client build. */
export interface MapSummary {
  id: string
  name: string
  territoryCount: number
  regionCount: number
  legacy?: 'warfish'
  addedOn?: string
  specialRules?: boolean
}

export interface MapDef {
  /** Vector territory fills show region colour; ownership and counts use discs. */
  territoryMarkers?: { style: 'circle'; radius: number }
  /** Recreation of an original warfish.net board. */
  legacy?: 'warfish'

  id: string
  name: string
  /** Optional map-specific rules shown at the top of the game's rules screen.
   *  Also the screen-reader text of the ★ rules overlay. */
  specialRules?: string[]
  /** The ★ special-rules overlay drawn over the live board (see ruleOverlay.ts). */
  ruleOverlay?: RuleOverlay
  territories: Territory[]
  regions: Region[]
  /** SVG viewBox: optional x/y offset + required width/height. */
  viewBox: { x?: number; y?: number; width: number; height: number }
  /**
   * Stable identity of the rendered map geometry. Generated from the static
   * map definition so derived client caches cannot survive a geometry update.
   */
  geometryVersion?: string
  /**
   * Sea-route line geometry generated by the map bundle pipeline. Optional so
   * older maps and API responses remain valid while clients can compute it.
   */
  seaRoutes?: MapSeaRoute[]
  /**
   * Border chevrons: one chevron centred on every border of these territories
   * where attacking in rolls custom dice (`borderDice`), pointing in. Red where
   * the attacker is better off (Royal Rumble: storming the Sand is d8), orange
   * where worse (Normulli Sector: plains attack towers at 5 v 7); standard 6 v 6
   * borders get none.
   */
  borderChevrons?: { territoryIds: string[] }
  /** Optional map key explaining the board's rule symbols (see MapLegend). */
  legend?: MapLegend
  /**
   * Draw troop counts at two fixed sizes, small and extra small, chosen per
   * territory by its `badgeSize` (`tieredBadgeFontSize`), so a board of very
   * different spaces reads uniform (Normulli Sector, whose wedges, towers and
   * crystals are far smaller than its plains). Absent: every count is the
   * usual size.
   */
  fitTroopBadges?: boolean
  /** Keep the dark-mode sea-route outline on light backgrounds too (Arctic). */
  darkSeaRoutes?: boolean
  /** When the map shipped (YYYY-MM-DD); the map pickers tag it NEW for 30 days after. */
  addedOn?: string
  /**
   * The map's own backdrop colour, per theme: fills the board's background and,
   * in the game, the map area and the strip above it (Atlantis Rising's sea,
   * Russia's tundra, …). Absent: the theme's paper. `topBar: true` also paints
   * the game's top bar in it.
   */
  backdropColor?: { light: string; dark: string; topBar?: boolean }
  /**
   * Chevron positions for `borderChevrons`, baked by the map bundle pipeline
   * (clients compute them when absent). `kind: 'disadvantage'` marks a border
   * where attacking in is worse for the attacker (drawn orange, not red).
   */
  borderChevronMarks?: Array<{ x: number; y: number; angle: number; kind?: 'disadvantage' }>
  /**
   * The board dictates its own opening position: fixed player count, fixed
   * ownership, fixed armies. When set, the lobby's manual/auto placement choice
   * and starting-troops setting do not apply. Absent on every classic map.
   */
  fixedSetup?: FixedSetup
  /**
   * Territory-specific opening garrisons that remain neutral during setup.
   * Values are fixed troop counts and do not change with the lobby's
   * `startingTroops` setting.
   */
  neutralArmyOverrides?: Partial<Record<TerritoryId, number>>
  /**
   * When true, MapView does NOT draw the sea-route / adjacency lines
   * between territories. For maps whose connections are conveyed by
   * other means (e.g. an explicit road network on Forbidden City),
   * the lines are visual clutter. Default: false / undefined (draw them).
   */
  hideAdjacencyLines?: boolean
  /** Show source/target inner glows during attack/fortify. Defaults to true. */
  selectionInnerGlow?: boolean
  /**
   * Extra breathing room baked into the viewBox by `pnpm map-editor:tighten`,
   * per side, as a fraction of that axis' geometry extent, on top of the
   * standard 2% margin. Unlike that margin it may extend past the previous
   * viewBox. Use sparingly: every unit of padding shrinks the playable map.
   */
  viewBoxPadding?: { top?: number; right?: number; bottom?: number; left?: number }
  /** Optional game chrome defaults. Old clients may ignore this presentation metadata. */
  gameLayout?: {
    /** Fraction of the portrait top bar occupied by the map (0..0.65). */
    topBarOverlap?: number
    playerBarPosition?: 'center' | 'right'
  }
  /**
   * Per-map edge-classifier + stroke-width calibration. Optional —
   * applied by tools/map-editor/apply-overrides.mjs from the editor's
   * calibration sliders.
   */
  edgeConfig?: {
    midEpsilon?: number
    angleEpsilonDeg?: number
    perimeterStrokeWidth?: number
    interiorStrokeWidth?: number
    bridgeStrokeWidth?: number
    boundaryFallbackWidth?: number
    regionBorderWidth?: number
    internalBorderWidth?: number
  }
  /**
   * Floating region-overlay toggle corner. Default 'bottom-right'.
   */
  regionToggleCorner?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  /**
   * Floating zoom (magnifying-glass) toggle corner. Optional — when
   * unset the zoom toggle stacks on top of the regions toggle inside
   * `regionToggleCorner`. The four corner values produce a free-
   * standing chip at that corner. `'right-of-regions'` renders the
   * zoom chip as a sibling INSIDE the regions toggle's slot, to its
   * right (horizontal row instead of vertical stack).
   */
  zoomToggleCorner?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'right-of-regions'
  /**
   * Optional SVG path string traced from the source bitmap's black
   * border network. When set, MapView renders this path ON TOP of the
   * territory polygons as a thin stroke — producing visible borders
   * between cells without modifying any cell geometry.
   */
  borderShape?: string
  /**
   * Pairs of territories whose adjacency is implied as crossing
   * "off-map" (e.g. across the international date line). MapView renders
   * each pair as TWO short dashed stubs instead of a single line drawn
   * straight across the whole map.
   */
  wraparoundAdjacencies?: Array<[TerritoryId, TerritoryId]>
  /**
   * Adjacency pairs that are genuinely land-adjacent but whose shared
   * border the geometric edge-classifier fails to detect (mismatched
   * SVG vertices), so MapView would otherwise draw a spurious dashed
   * "sea route" between them. Listing a pair here suppresses that
   * connector. Canonical order doesn't matter.
   */
  suppressSeaRoutes?: Array<[TerritoryId, TerritoryId]>
  /**
   * The inverse of `suppressSeaRoutes`: adjacency pairs that MUST render as a
   * dashed water crossing even though their polygons touch, so the geometric
   * classifier calls them land.
   *
   * Needed when two bodies of water are genuinely joined but the crossing
   * between them is the whole point. On Great Lakes the traced lake outlines
   * meet at the St. Marys, Mackinac and Niagara straits, so all three came out
   * as ordinary internal borders — the map read as two big lakes instead of
   * five joined by four chokepoints, and the thing a player most needs to see
   * (there is exactly ONE way from Michigan into Huron) was invisible.
   *
   * For a forced pair whose polygons touch, MapView anchors the dash to the
   * SHARED BORDER (`sharedBorderSegment`) rather than the closest vertex pair,
   * which would be zero-length. Listing a pair that doesn't touch is harmless —
   * it would have been a sea route anyway. Canonical order doesn't matter.
   */
  forceSeaRoutes?: Array<[TerritoryId, TerritoryId]>
  /**
   * Optional per-pair visual tuning for `forceSeaRoutes`, keyed by the two ids
   * sorted and joined with "|" (e.g. `'long-point|niagara-bar'`).
   *
   * A forced crossing is drawn as a short mark straddling the shared border,
   * aimed perpendicular to it. That is the right default, but "perpendicular to
   * the border" is not always what looks right: where two lakes meet along a
   * slightly angled edge the mark comes out a few degrees off vertical, which
   * reads as misaligned even though it is geometrically correct. Rather than
   * keep re-tuning the algorithm for one map, these are the knobs:
   *
   *   angleDeg — force an absolute angle instead of perpendicular-to-border.
   *              0 = pointing right, 90 = pointing down (SVG y grows downward),
   *              so 90 draws a vertical mark.
   *   endpointMode — `'edge-midpoints'` aims the route at the middle of the
   *                  closest polygon edges instead of their vertices.
   *                  `'centers'` runs it between the two territories' centres,
   *                  clipped to their coasts, so a landing or strait reads as
   *                  "this territory to that one" rather than corner to corner.
   *   offset   — nudge the midpoint, in viewBox units.
   *   length   — override the mark's length, in viewBox units.
   *
   * Purely cosmetic: none of this affects adjacency or play.
   */
  forceSeaRouteTuning?: Record<
    string,
    { endpointMode?: 'edge-midpoints' | 'centers'; angleDeg?: number; offset?: { x?: number; y?: number }; length?: number }
  >
  /**
   * Decorative non-territory regions painted ABOVE the continent
   * background but BELOW the territory polygons. Used for natural
   * features like lakes / inland seas that don't participate in the
   * game graph. `fill` is any valid SVG colour (hex like '#3a6ea5').
   *
   * `role: 'warfish-logo'` marks the parts of the Warfish.net logo on Warfish
   * Classics boards: tapping them opens the Warfish.net classics explainer,
   * and they dim with the board under selection and region spotlights.
   */
  decorations?: Array<{ id: string; shape: string; fill: string; role?: 'warfish-logo' }>
  /**
   * Optional raster backdrop rendered BELOW the territory polygons, filling
   * the whole viewBox (pans/zooms with the board). The value is a bundled
   * image key resolved by the client (mobile: `bundledMapImages`; editor:
   * served PNG) — e.g. `'captureflag'`. Used for hand-drawn/isometric maps
   * where the art is a bitmap and territories are invisible hit-regions on top.
   */
  backgroundImage?: string
  /**
   * When true, ownership colours on this raster-backed map are painted opaque
   * so a light territory base in the artwork cannot wash out the player
   * palette. Other backdrop maps retain the normal translucent fills.
   */
  opaqueTerritoryFills?: boolean
  /**
   * When true, the map explorer's region view (`showRegionBonuses`) draws this
   * map's raster art (backgroundImage and wallsOverlay) in greyscale, so
   * coloured scenery such as lava and gold walls steps back with the
   * territories and only the region tints carry colour.
   */
  greyArtInRegionView?: boolean
  /**
   * When true, this raster-backed map's art draws every region border itself, so
   * MapView skips the region perimeter overlay (the region-colour stroke just
   * inside each region's edge), which would otherwise show as a thin coloured
   * line between a fill and the art's own border (Normulli Sector's walls).
   */
  regionBordersInArt?: boolean
  /**
   * When true, this raster-backed map draws the borders between territories
   * of one region in the region colour at the full
   * `edgeConfig.internalBorderWidth`, as a vector map does, instead of the
   * thin (0.35×) line other backdrop maps lay over their art. For art whose
   * source draws each region's internal borders in its own colour (Crescent
   * Isle).
   */
  regionColoredInternalBorders?: boolean
  /**
   * Optional raster overlay rendered ON TOP of the territory polygons — used
   * with `backgroundImage` to redraw the walls above the territory tints so
   * territory colour never bleeds onto a wall. Same bundled-key resolution as
   * `backgroundImage`. Transparent everywhere except the wall pixels.
   */
  wallsOverlay?: string
  /**
   * When true, MapView skips the per-region continent backdrop.
   */
  noContinentFill?: boolean
  /**
   * When true, this map is for automated testing only and must NOT
   * appear in the production lobby map picker. The GET /maps list
   * endpoint filters it out in production; non-production servers include it
   * automatically, and `?includeTest=false` narrows to the production shape.
   * The engine's `getMap` function and GET /maps/:id are unaffected —
   * server code and Maestro flows can still retrieve it by id.
   */
  testOnly?: boolean
  /**
   * Withhold this map from the lobby map picker entirely — a work-in-progress
   * board that isn't ready to be played on.
   *
   * DIFFERENT FROM `testOnly`, which means "a test fixture, hidden in production
   * but wanted on dev so Maestro can use it". `hidden` is stronger and simpler:
   * `GET /api/v1/maps` omits it on EVERY environment, so nobody can start a game
   * on it by accident, including us in dev.
   *
   * It stays fully retrievable by id (`GET /api/v1/maps/:id`, `getMap`), so the
   * map pipeline, the map editor and any game already using it keep working —
   * hiding a map must not break a game in progress.
   */
  hidden?: boolean
}

/**
 * Return a stable, versioned identity for the geometry and static rendering
 * inputs of a map. Mutable opening/game state and display-only copy are
 * deliberately excluded: changing those must not invalidate geometry caches.
 *
 * This is intentionally a small portable hash rather than a platform crypto
 * primitive because MapDef is shared by Node, Metro, the web build, and the
 * map-editor extraction script.
 */
export function mapGeometryVersion(map: MapDef): string {
  const {
    name: _name,
    specialRules: _specialRules,
    ruleOverlay: _ruleOverlay,
    fixedSetup: _fixedSetup,
    neutralArmyOverrides: _neutralArmyOverrides,
    geometryVersion: _geometryVersion,
    seaRoutes: _seaRoutes,
    ...mapGeometry
  } = map
  const geometry = {
    ...mapGeometry,
    // Territory ownership and armies are live game state, not map geometry.
    // Excluding them keeps the fallback identity stable across every turn.
    territories: map.territories.map(({ ownerId: _ownerId, armies: _armies, ...territory }) => territory),
  }
  const input = stableGeometryJson(geometry)
  let hashA = 0x811c9dc5
  let hashB = 0x9e3779b9
  for (let i = 0; i < input.length; i++) {
    const code = input.charCodeAt(i)
    hashA ^= code
    hashA = Math.imul(hashA, 0x01000193)
    hashB ^= code + i
    hashB = Math.imul(hashB, 0x85ebca6b)
  }
  return `v1-${(hashA >>> 0).toString(16).padStart(8, '0')}-${(hashB >>> 0).toString(16).padStart(8, '0')}`
}

function stableGeometryJson(value: unknown): string {
  if (value === null) return 'null'
  if (value === undefined) return 'undefined'
  if (typeof value !== 'object') return JSON.stringify(value)
  if (Array.isArray(value)) return `[${value.map(stableGeometryJson).join(',')}]`

  const object = value as Record<string, unknown>
  return `{${Object.keys(object)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableGeometryJson(object[key])}`)
    .join(',')}}`
}

export type Phase = 'claim' | 'placement' | 'attack' | 'fortify' | 'post-elim-placement'

/**
 * A discriminated union of every meaningful action that changes game state.
 * Appended by the engine `apply*` functions; stored in `GameState.events`.
 *
 * Privacy notes:
 *  - `card-draw` omits card identity so opponents cannot see what was drawn.
 *  - `card-turn-in` omits card identities for the same reason.
 */
/**
 * Fog of war, server-only: for each territory an event touches, the players
 * who could see it when the event was saved. Lets every player's log keep
 * exactly what they could see at the time, unchanged as the fog later moves.
 * Written by `annotateFogVisibility` (packages/api-types/src/fog.ts) and
 * stripped before any client receives the event. A fogged viewer's copy may
 * carry '' for a territory they couldn't see ("out of sight").
 */
export type FogSeenBy = Record<TerritoryId, PlayerId[]>

export type GameEvent =
  // `armies` = the reinforcement the player receives on entering placement, as
  // computed at turn-start (territory base + region bonuses + banked
  // bonusArmies). OPTIONAL: events logged before this field existed omit it, and
  // the log formatter drops the parenthetical when it's absent rather than
  // printing "(undefined troops)". A later card turn-in adds more and logs its
  // own row, so this stays "what they started the turn with".
  | { kind: 'turn-start'; player: PlayerId; turnNumber: number; armies?: number; ts: number }
  | { kind: 'phase-advance'; from: Phase; to: Phase; player: PlayerId; ts: number }
  | { kind: 'claim'; player: PlayerId; territoryId: TerritoryId; armies: number; seenBy?: FogSeenBy; ts: number }
  | { kind: 'placement'; player: PlayerId; placements: Array<{ territoryId: TerritoryId; armies: number }>; seenBy?: FogSeenBy; ts: number }
  // `unseen` is set only on a fog-of-war viewer's copy of an attack with one
  // end out of view: no dice (empty rolls), 0 losses for that end, and in
  // heavy fog '' for that territory (and usually its player).
  // `isFriendlyFire` is set (true) only in team games when the defender is the
  // attacker's teammate. Absent otherwise, so free-for-all events are unchanged.
  | { kind: 'attack'; attacker: PlayerId; defender: PlayerId; fromTerritoryId: TerritoryId; toTerritoryId: TerritoryId; roll: DiceRoll; attackerLost: number; defenderLost: number; conquered: boolean; isFriendlyFire?: true; unseen?: 'from' | 'to'; seenBy?: FogSeenBy; ts: number }
  // `armiesMoved` is absent only in a fog-of-war viewer's copy (light fog shows
  // conquests everywhere but not their troops). The engine always sets it.
  | { kind: 'conquest'; attacker: PlayerId; defender: PlayerId; territoryId: TerritoryId; armiesMoved?: number; seenBy?: FogSeenBy; ts: number }
  | { kind: 'post-conquest-transfer'; player: PlayerId; fromTerritoryId: TerritoryId; toTerritoryId: TerritoryId; armies: number; seenBy?: FogSeenBy; ts: number }
  // `cardsInherited` / `bonusGained` are absent only in a fog-of-war viewer's
  // copy when the eliminator is not on their side. The engine always sets them.
  | { kind: 'elimination'; eliminator: PlayerId; eliminated: PlayerId; cardsInherited?: number; bonusGained?: number; ts: number }
  | { kind: 'card-draw'; player: PlayerId; ts: number }
  | { kind: 'card-turn-in'; player: PlayerId; bonus: number; ts: number }
  | { kind: 'fortify'; player: PlayerId; fromTerritoryId: TerritoryId; toTerritoryId: TerritoryId; armies: number; seenBy?: FogSeenBy; ts: number }
  | { kind: 'resign'; player: PlayerId; ts: number }
  // Team games add `winnerTeam` (null on a tie) and `winners`: every member of
  // the winning team in turn order, including members eliminated earlier.
  // `winner` stays the first of `winners`. Both are absent in free-for-all.
  | { kind: 'game-end'; winner: PlayerId | null; reason: 'last-standing' | 'stalled' | 'resign'; winnerTeam?: TeamId | null; winners?: PlayerId[]; ts: number }
  // Team games: the last member of `team` still holding territory went out
  // (eliminated in combat or resigned). Follows that player's own event.
  | { kind: 'team-eliminated'; team: TeamId; lastPlayer: PlayerId; ts: number }
  // A player who stopped taking turns had their seat handed to an AI for the
  // rest of the game. `player` is the seat (now played by the bot); the names
  // are captured at takeover time because the seat's display name changes to
  // the bot's. `reason`: 'turn-timeout' when their time expired (triggered
  // manually by an admin today, automatic once turn timers ship) or 'abandoned'
  // when the player chose Abandon instead of Resign.
  | { kind: 'ai-takeover'; player: PlayerId; playerName: string; botProfile: string; botName: string; reason: 'turn-timeout' | 'abandoned'; bootedBy?: PlayerId; bootedByName?: string; ts: number }
  // Wire-only, fog-of-war games: stands in 1:1 for a move the viewer could not
  // see (so event counts and absolute indices stay aligned). Never produced by
  // the engine or persisted. `followUp`: it was a conquest or post-conquest
  // move, which the log folds into the attack's row like the real event.
  | { kind: 'hidden'; player: PlayerId; followUp?: true; ts: number }

/** One territory's live slice on the wire. See GameState.territories. */
export interface WireTerritory {
  id: TerritoryId
  ownerId?: PlayerId
  armies?: number
  fogged?: true
}

export interface GameState {
  /**
   * The id of the map used for this game. The full MapDef (territory
   * geometry, region defs, adjacency lists) is fetched separately via
   * GET /maps/:id which has staleTime: Infinity on the client. This
   * decoupling eliminates 80–284 KB of duplicated geometry from every
   * poll response.
   *
   * Defensive note: legacy game rows written before the Tier-3 perf change
   * carry `state.map: MapDef` in the DB column. The server's formatGame
   * strips it on read — it never reaches the wire. Always use `mapId` here.
   */
  mapId: string
  /**
   * Per-territory mutable state: current ownership and troop count.
   * The static geometry (name, shape, position, adjacencies, regionId) is
   * in the MapDef fetched via GET /maps/:mapId. Clients merge the two:
   *   mergedTerritories = mapDef.territories.map(t => ({
   *     ...t, ...territories.find(x => x.id === t.id)
   *   }))
   *
   * Absent on LOBBY games (state is null before the game starts).
   * May also be absent on very old legacy rows — treat as empty array.
   */
  // In fog-of-war games, territories outside the viewer's vision arrive as
  // `{ id, fogged: true }` (heavy fog) or `{ id, ownerId, fogged: true }`
  // (light fog): the server never sends what the viewer can't see.
  territories?: Array<WireTerritory>
  players: PlayerId[]
  turnIndex: number
  phase: Phase
  eliminated: PlayerId[]
  /** Players who left by resigning (a subset of `eliminated`). */
  resigned?: PlayerId[]
  /**
   * Team membership, set once at game start and fixed for the game. Absent in
   * free-for-all games. Read through the helpers in teams.ts (`teamOf`,
   * `areAllies`, `isAlly`), never directly.
   */
  teams?: TeamMembership
  config: GameConfig
  attacksThisTurn: number
  fortifiesThisTurn: number
  /**
   * Per-player count of starting armies still to be placed during the `claim` phase.
   * Initialized to `config.startingTroops` for each player at game start. Decremented
   * by 1 on each successful claim action. Once every entry is 0, the engine
   * transitions to the `reinforce` phase and this field stays at 0 thereafter.
   */
  armiesToPlace: Record<PlayerId, number>
  /** Number of complete rounds played (full pass through all non-eliminated players = 1 round). Starts at 0. */
  roundsPlayed: number
  /**
   * Set immediately after a conquering attack — enables the
   * post-conquest army transfer ("free fortify") window via
   * POST /turn/transfer. Cleared by the next attack, advancePhase,
   * or resign by the current player. Optional for backward
   * compatibility with games whose state predates this field.
   */
  lastConquest?: {
    fromTerritoryId: TerritoryId
    toTerritoryId: TerritoryId
    armiesAlreadyMoved: number
  } | null
  // ── Cards ────────────────────────────────────────────────────────────────
  /** Per-player card hand. Initialized empty per player at game start. */
  cards?: Record<PlayerId, Card[]>
  // The dice seed, draw pile and discard pile are server-only and never sent
  // to clients (lib/formatGame.ts normaliseStateForWire); see the engine
  // GameState for them.
  /** Total successful set turn-ins across the whole game. */
  cardTurnIns?: number
  /**
   * Per-player placement-budget bonus from card turn-ins + eliminate-player
   * bonuses. Spent on the player's next placement; zeroed on placement→attack.
   */
  bonusArmies?: Record<PlayerId, number>
  /** True if the active player has captured ≥1 territory during the current turn. */
  conqueredThisTurn?: boolean
  /**
   * Set true at placement-phase entry when the player has ≥5 cards. While
   * set, placement is rejected until the player turns in a set and drops
   * back to ≤4 cards.
   */
  mustTurnInCards?: boolean
  /**
   * Append-only event log. Every engine `apply*` function appends events here.
   * Optional for backward compatibility with games whose persisted state
   * predates this field — treat `undefined` the same as `[]`.
   *
   * Wire note: on the wire this is TRIMMED to the most-recent WIRE_EVENT_LIMIT
   * entries (a tail for the "recent activity" glance), while `eventCount` holds
   * the true total. The time-machine / log screens fetch the FULL log from
   * `GET /games/:id/events` and reconstruct from it, mapping a tail row to its
   * absolute index via `offset = eventCount - events.length`. In the persisted
   * engine state (not the wire) this is the full append-only log.
   */
  events?: GameEvent[]
  /**
   * True total number of events in the persisted log. Always present on wire
   * responses from `GET /games/:id`, WebSocket `GAME_UPDATED`/`GAME_STATE_PATCH`,
   * and the games list. Use this for totals and as the log endpoint's starting
   * offset. May be absent on very old legacy responses — treat `undefined` as 0.
   */
  eventCount?: number
  /**
   * Snapshot of every territory's starting ownership + armies, captured
   * once at game start. Combined with `events`, this is enough to
   * reconstruct map state at any historical point — drives the
   * time-machine log view in the mobile client.
   *
   * Only `{ id, ownerId, armies }` per territory; everything else
   * (name, shape, adjacencies, position) comes from the static MapDef.
   * Optional for backward-compat with games whose state predates the field.
   */
  initialTerritories?: Array<{ id: TerritoryId; ownerId: PlayerId; armies: number }>
}

/**
 * GET /games/:id/events — full event log response.
 *
 * Returns the persisted event log (the wire game-state only carries a recent
 * tail). With `?since=N` it returns the contiguous suffix `events[N..]` for a
 * cheap delta fetch (the log is append-only); without it, the full log.
 *
 * `events`     — the slice from `since` onward (the full log when since=0).
 * `eventCount` — the TRUE total. The client appends the delta to its cache and
 *                resyncs (re-fetches since=0) if the merged length ≠ eventCount.
 * `since`      — the offset the server actually used (clamped to [0, total]);
 *                lets the client confirm the delta aligns with its cache.
 *
 * LOBBY games (or games with no state) return `{ events: [], eventCount: 0, since: 0 }`.
 *
 * Access control mirrors `GET /games/:id`: members always have access; for
 * LOBBY games the relaxed rules apply (public games + invited non-members);
 * ACTIVE/COMPLETE games are members-only.
 */
export interface GameEventsResponse {
  events: GameEvent[]
  eventCount: number
  /** Offset the server sliced from (clamped to [0, eventCount]). 0 = full log. */
  since?: number
}

// ─── Chat ─────────────────────────────────────────────────────────────────────

/** Curated reaction picker shared by the API and clients. Quick actions use the first two. */
export const CHAT_REACTION_EMOJIS = [
  '👍', '👎', '❤️', '😂', '😮', '😢', '🎉',
  '🔥', '✅', '🙌', '👏', '😆', '😍', '🤔',
  '👀', '⚔️', '🛡️', '🐟', '🦈', '🤝', '💪',
  '😡', '🤯', '😎', '🙏', '💀', '🚀', '🗺️',
  '🏆', '🫡', '🤣', '😭', '🤡', '🐐', '🍿',
] as const
export type ChatReactionEmoji = (typeof CHAT_REACTION_EMOJIS)[number]

export interface MessageReactionResponse {
  emoji: ChatReactionEmoji
  userId: string
  displayName: string
}

export interface ChatMessageResponse {
  id: string
  gameId: string
  userId: string
  displayName: string
  content: string
  createdAt: string
  reactions: MessageReactionResponse[]
}

export interface PostChatMessageRequest {
  content: string
}

export interface ToggleMessageReactionRequest {
  emoji: ChatReactionEmoji
}

/** Backward-compatible public-chat name; chat and DMs share one palette. */
export type ToggleChatReactionRequest = ToggleMessageReactionRequest

export interface ChatReactionUpdateResponse {
  messageId: string
  reactions: MessageReactionResponse[]
}

// ─── Direct (secret) messages ──────────────────────────────────────────────────
//
// Private 1:1 messages between two players in the same game. Unlike ChatMessage
// (public, broadcast game-wide) a DirectMessage is only ever delivered to the
// sender and recipient. `readAt` drives per-thread unread badges.

export interface DirectMessageResponse {
  id: string
  gameId: string
  fromUserId: string
  fromDisplayName: string
  toUserId: string
  content: string
  createdAt: string
  /** ISO timestamp when the recipient read it, or null while unread. */
  readAt: string | null
  reactions: MessageReactionResponse[]
}

export interface DirectMessageReactionUpdateResponse {
  messageId: string
  reactions: MessageReactionResponse[]
}

/** One conversation summary for the DM inbox / roster unread badges. */
export interface DirectMessageThreadSummary {
  /** The OTHER participant (not the requesting user). */
  otherUserId: string
  otherDisplayName: string
  /** Most recent message in the thread (either direction), or null if none. */
  lastMessage: DirectMessageResponse | null
  /** Count of messages sent TO the requesting user that are still unread. */
  unreadCount: number
}

export interface DirectMessageThreadsResponse {
  threads: DirectMessageThreadSummary[]
}

export interface DirectMessageThreadResponse {
  messages: DirectMessageResponse[]
  otherUsername?: string
  /** False when this conversation cannot accept messages; does not disclose who blocked whom. */
  messagingAvailable?: boolean
  /** Only the viewer's own block decision, never the other player's. */
  viewerHasBlocked?: boolean
}

export interface PostDirectMessageRequest {
  content: string
}

// ─── Team chat (secret, one channel per team) ──────────────────────────────────
//
// A team game gives each team one secret group chat. Its members are the
// team's human seats that have not resigned (`teamChatTeam` in teams.ts);
// teammates knocked out in combat stay in it. Messages go only to those
// members' sockets, never to other teams, spectators or the public game chat.

export interface TeamMessageResponse {
  id: string
  gameId: string
  team: TeamId
  fromUserId: string
  fromDisplayName: string
  content: string
  createdAt: string
}

/** One member of a team chat, for the thread header and sender colours. */
export interface TeamChatMemberResponse {
  userId: string
  displayName: string
  /** The member's seat (engine player id as a number). */
  slot: number
  /** Chosen colour, or null for legacy rows (fall back to `slot`). */
  colorSlot: number | null
}

/** GET /games/:id/team-chat: the viewer's team thread. */
export interface TeamChatResponse {
  team: TeamId
  members: TeamChatMemberResponse[]
  /** Oldest first; the most recent 200. */
  messages: TeamMessageResponse[]
  /**
   * When the viewer last read the thread, or null if never. Messages from
   * others created after it are unread.
   */
  lastReadAt: string | null
  /** Unread messages from teammates (server count at fetch time). */
  unreadCount: number
  /** False once the game is over: the thread stays readable but read-only. */
  messagingAvailable: boolean
}

export interface PostTeamMessageRequest {
  content: string
}

/** POST /games/:id/team-chat/read. */
export interface TeamChatReadResponse {
  lastReadAt: string
}

// ─── Contact ──────────────────────────────────────────────────────────────────

/**
 * Best-effort client environment, attached to every contact submission so the
 * maintainer can triage bug reports / feature requests without a round-trip.
 * All fields optional — collected from what's already bundled (no native
 * device module), so any of them can be missing on a given platform.
 */
export interface ContactDeviceInfo {
  /** 'ios' | 'android' | 'web' (Platform.OS). */
  platform?: string
  /** OS version string (Platform.Version). */
  osVersion?: string
  /** App version (expo.version, e.g. '0.9.580'). */
  appVersion?: string
  /** Device name/model when available (Constants.deviceName). */
  deviceModel?: string
  /** Browser user-agent — only meaningful on web. */
  userAgent?: string
}

export interface ReportMessageReference {
  kind: 'chat' | 'dm'
  messageId: string
}

export interface ReportMessageContext extends ReportMessageReference {
  alreadyReported?: boolean
  gameId: string
  gameName: string | null
  authorId: string
  authorName: string
  content: string
  createdAt: string
  link: string
}

export interface ContactRequest {
  reason: 'bug' | 'feature' | 'feedback' | 'player-report'
  /** Required for player-report; identity is resolved server-side. */
  reportedUserId?: string
  reportedMessage?: ReportMessageReference & { link: string }
  reportedDmConversation?: { gameId: string; link: string }
  message: string
  /** Optional client environment; older clients omit it. */
  deviceInfo?: ContactDeviceInfo
}

// ─── Invites ─────────────────────────────────────────────────────────────────

export interface AppInviteRequest {
  email: string
  message?: string
}

// ─── Admin ────────────────────────────────────────────────────────────────────

export type AdminUserSort = 'active' | 'joined' | 'username-asc' | 'username-desc'
export interface AdminUserSummary {
  id: string
  username: string
  displayName: string
  createdAt: string
  lastActiveAt: string | null
}
export interface AdminUsersResponse {
  users: AdminUserSummary[]
  nextCursor: string | null
}
export interface AdminUserProfile extends AdminUserSummary {
  email: string
  realName: string | null
  emailVerified: boolean
  signupIp: string | null
  lastIp: string | null
  gamesPlayed: number
  wins: number
  losses: number
  emailNotifications: boolean
  pushNotifications: boolean
  /** Resolved category preferences. Older API deployments may omit this field. */
  notificationSettings?: NotificationSettings
  gamesJoined: number
  ticketsSubmitted: number
  /** Games where an AI took over this player's seat after they stopped taking turns. */
  gamesAbandoned: number
  /** First recorded events; null is unknown, not a historical inference. */
  tutorialStartedAt?: string | null
  tutorialCompletedAt?: string | null
  practiceGameCompletedAt?: string | null
  automatedGameInvitedAt?: string | null
}

export interface AdminGamePlayer {
  userId: string
  username: string
  displayName: string
  isBot: boolean
  /** Permanent bot catalog key; null for humans. */
  botProfile: string | null
  /** True for the seat whose turn it currently is. */
  current: boolean
}
export interface AdminGameSummary {
  id: string
  name: string | null
  mapId: string | null
  isRanked: boolean
  isPublic: boolean
  turn: number
  startedAt: string | null
  turnStartedAt: string | null
  players: AdminGamePlayer[]
}
export interface AdminGamesResponse {
  games: AdminGameSummary[]
  nextCursor: string | null
}
export interface AdminAiTakeoverPreview {
  gameId: string
  userId: string
  slot: number
  playerName: string
  /** The bot that takes the seat. Null only when the seat resigns instead (`resignsInstead`). */
  botProfile: string | null
  botName: string | null
  /**
   * Rollback only: with `BOTS_IN_TEAM_GAMES_ENABLED=0` a team game's seat
   * resigns instead of being taken over (its territories go neutral). Absent
   * otherwise: team games are taken over like individual games
   * (docs/bots/team-and-fog-bots-plan.md, decision 2).
   */
  resignsInstead?: true
  /** True when it is this player's turn now, so the AI starts playing immediately. */
  isCurrentTurn: boolean
  /** When the game's current turn began (whoever's turn it is). Null for games from before turn tracking. */
  turnStartedAt: string | null
  /** How long it has been THIS player's turn, in ms. Null when it isn't their turn (or the start is unknown). */
  turnElapsedMs: number | null
  /** turnElapsedMs as a short label, e.g. "3h 12m", "2d 4h", "45s". Null when turnElapsedMs is null. */
  turnElapsedLabel: string | null
}
export type AdminAiTakeoverResponse =
  | { dryRun: true; preview: AdminAiTakeoverPreview }
  | { dryRun: false; result: AdminAiTakeoverPreview }

/** One player's lifetime turns after a turn-time rebuild. */
export interface AdminTurnRebuildUser {
  userId: string
  username: string
  turns: number
  averageTurnSeconds: number | null
}

/**
 * POST /api/v1/admin/turn-times/rebuild. Recomputes every seat's and player's
 * turn totals from the event logs, as if turn tracking had always existed.
 * Dry run unless `confirm: true`. Idempotent: totals are set, not incremented.
 */
export interface AdminTurnRebuildResponse {
  dryRun: boolean
  /** True when the totals were written. */
  applied: boolean
  gamesWithLogs: number
  /** Started games past event retention; their turns can't be recovered. */
  gamesWithoutLogs: number
  /** Purged games whose seats already hold tracked turns; while non-empty, writing is refused. */
  blockingGames: string[]
  seatTurns: number
  userTurns: number
  /** Players by lifetime turns, most first (at most 50). */
  users: AdminTurnRebuildUser[]
}

export type SupportTicketType = 'PLAYER_REPORT' | 'BUG' | 'FEATURE' | 'FEEDBACK' | 'ADMIN_MESSAGE' | 'NEWS'

export interface AdminDashboard {
  generatedAt: string
  /** Conversations with player activity this admin hasn't read. Warcarp's own messages never count. */
  unreadMessages: number
  /** Conversations this admin starred. */
  starredConversations: number
  activePlayers7d: number
  totalPlayers: number
  /** Human players who joined since `newPlayersSince` (7 days before generatedAt). */
  newPlayers7d: number
  newPlayersSince: string
  activeGames: number
  lobbyGames: number
}

export interface AdminNewMessageRequest {
  type?: 'ADMIN_MESSAGE' | 'NEWS'
  recipientId: string
  subject: string
  body: string
  clientId: string
}
export interface NewsPublication {
  id: string
  subject: string
  body: string
  createdAt: string
  total: number
  delivered: number
  completedAt: string | null
}
export interface SupportTicket {
  /** Submitter removed the conversation; public replies are disabled. */
  submitterDeletedAt?: string | null
  id: string
  type: SupportTicketType
  subject: string
  submittedById: string | null
  relatedUserId: string | null
  description: string
  context: {
    reporter: { id: string; username: string; displayName: string; email: string }
    /** For ADMIN_MESSAGE, reporter/submittedById identify the recipient. */
    initiatedBy?: { id: string; username: string }
    /** Type-specific context for PLAYER_REPORT tickets. */
    reportedUser?: { id: string; username: string }
    message?: Omit<ReportMessageContext, 'alreadyReported'>
    conversation?: { kind: 'dm'; gameId: string; gameName: string | null; link: string }
    deviceInfo?: ContactDeviceInfo
  }
  notificationSentAt: string | null
  notificationError: string | null
  createdAt: string
  updatedAt: string
}

/** Consistent report titles in inbox previews and conversation headers. */
export function supportTicketSubject(ticket: { type: string; subject: string; context?: unknown }): string {
  const context = ticket.context
  if (ticket.type === 'PLAYER_REPORT' && context && typeof context === 'object' && 'reportedUser' in context) {
    const user = context.reportedUser
    if (user && typeof user === 'object' && 'username' in user && typeof user.username === 'string' && user.username) {
      return `Player reported: @${user.username}`
    }
  }
  return ticket.subject
}

export interface AdminSupportTicketsResponse {
  tickets: SupportTicket[]
  nextCursor: string | null
}

export interface SupportInboxItem {
  id: string
  subject: string
  sender: string
  preview: string
  type: SupportTicketType
  lastMessageAt: string
  unread: boolean
}
export interface SupportInboxResponse {
  items: SupportInboxItem[]
  nextCursor: string | null
}
export type AdminInboxItem = SupportInboxItem & {
  /** Starred by this admin. */
  starred: boolean
}
export interface AdminInboxResponse {
  items: AdminInboxItem[]
  nextCursor: string | null
}

export interface SupportMessage {
  id: string
  ticketId: string
  authorId: string | null
  authorName: string
  fromAdmin: boolean
  internal: boolean
  body: string
  clientId: string
  createdAt: string
}

export interface SupportMessagesResponse {
  messages: SupportMessage[]
  nextCursor: string | null
  /** This viewer's persisted read position, never the other participant's. */
  readThrough?: string | null
}

export interface CreateSupportMessageRequest {
  body: string
  /** Stable per send; reuse on retry to avoid duplicate replies. */
  clientId: string
  internal?: boolean
}

/** Compatibility shape for the original admin report endpoints. */
export interface AdminPlayerReport extends Omit<SupportTicket, 'type' | 'subject' | 'submittedById' | 'relatedUserId' | 'description'> {
  reporterId: string | null
  reportedUserId: string | null
  explanation: string
}
export interface AdminPlayerReportsResponse {
  reports: AdminPlayerReport[]
  nextCursor: string | null
}

export interface AdminBotGameCleanupRequest {
  username: string
  action?: 'delete' | 'unrank'
  excludeGameIds?: string[]
  cursor?: string
  limit?: number
  confirm?: boolean
  /** Apply only these previously previewed IDs, never a fresh broad search. */
  gameIds?: string[]
  previewToken?: string
}

export interface AdminBotGameCleanupResponse {
  dryRun: boolean
  action: 'delete' | 'unrank'
  username: string
  userId: string
  matched: Array<{ id: string; name: string | null; status: GameStatus; bots: number; url: string; isRanked: boolean }>
  skipped: Array<{ id: string; name: string | null; status: GameStatus; url: string; reason: string }>
  nextCursor: string | null
  previewToken: string | null
  deleted: number
  unranked: number
  counts: {
    players: number; events: number; snapshots: number; invitations: number
    messages: number; directMessages: number; chatReactions: number; directReactions: number
    teamMessages: number
  }
  stats: {
    before: { gamesPlayed: number; wins: number; losses: number }
    removed: { gamesPlayed: number; wins: number; losses: number }
    after: { gamesPlayed: number; wins: number; losses: number }
  }
}

export interface AdminDeleteGameResponse {
  deleted: true
  gameId: string
  counts: {
    turns: number
    players: number
    chatMessages: number
  }
}

export interface AdminBulkDeleteResponse {
  dryRun: boolean
  matched: Array<{ id: string; status: GameStatus }>
  deleted: number | null
}

export interface AdminListGamesResponse {
  games: Array<{
    id: string
    name: string | null
    status: GameStatus
    adminId: string
    adminEmail: string | null
    playerCount: number
    createdAt: string
    completedAt: string | null
  }>
  nextCursor: string | null
}

export interface AdminStatusResponse {
  isAdmin: boolean
}

/**
 * POST /api/v1/admin/notify-stale-turns
 *
 * Response listing every stale-turn candidate and how many were notified.
 * In dryRun mode the games array lists candidates but nothing is sent or stamped.
 */
export interface AdminNotifyStaleTurnsResponse {
  dryRun: boolean
  thresholdHours: number
  /** Number of ACTIVE games whose turnStartedAt was beyond the threshold. */
  checked: number
  /** Number of games actually notified (0 in dryRun). */
  notified: number
  games: Array<{
    id: string
    name: string | null
    currentPlayerUserId: string
    /** Hours elapsed since turnStartedAt, rounded to nearest integer. */
    hoursStale: number
  }>
}

// ─── Errors ───────────────────────────────────────────────────────────────────

export interface ApiError {
  statusCode: number
  error: string
  message: string
}

/**
 * 400 body returned when a requested player name is already taken.
 * Returned by PATCH /api/v1/users/me when displayName conflicts with an
 * existing username or displayName (case-insensitive check across both columns).
 */
export interface NameTakenError {
  statusCode: 400
  error: 'NAME_TAKEN'
  message: string
}

// ─── WebSocket Events ─────────────────────────────────────────────────────────

export type ServerEvent =
  | { type: 'GAME_UPDATED'; game: GameResponse }
  | { type: 'GAME_DELETED'; gameId: string }
  | { type: 'PLAYER_JOINED'; player: GamePlayerResponse }
  | { type: 'TURN_CHANGED'; currentPlayerIndex: number; currentTurn: number }
  /**
   * Private turn alert. Delivered only on the authenticated recipient's
   * per-user channel, never on the game-wide channel.
   */
  | { type: 'YOUR_TURN'; gameId: string; gameName: string; turn: number | null; occurredAt: number }
  /**
   * The game ended. `winnerSlot` is the winner (in a team game the winning
   * team's first member in turn order); `tied` lists the tied players on a
   * stalled tie. Team games add `winnerTeam` (null on a tie) and
   * `winnerSlots`: every member of the winning team, including members
   * eliminated earlier. Both are absent in free-for-all.
   */
  | { type: 'GAME_COMPLETE'; game: GameResponse; winnerSlot?: string; tied?: string[]; winnerTeam?: TeamId | null; winnerSlots?: string[] }
  | { type: 'CHAT_MESSAGE'; message: ChatMessageResponse }
  | { type: 'CHAT_REACTION_UPDATED'; gameId: string; messageId: string; reactions: MessageReactionResponse[] }
  /**
   * A private 1:1 message. Delivered ONLY on the per-user channel to the
   * sender and recipient (never broadcast to the whole game), so the content
   * never reaches other players' sockets. Clients key cache updates off
   * `message.gameId` (which may differ from the socket's own game when a user
   * has multiple game sockets open).
   */
  | { type: 'DIRECT_MESSAGE'; message: DirectMessageResponse }
  /**
   * A secret team chat message. Delivered ONLY on the per-user channels of
   * the team's chat members (sender included), never game-wide. Like
   * DIRECT_MESSAGE, key cache updates off `message.gameId`.
   */
  | { type: 'TEAM_MESSAGE'; message: TeamMessageResponse }
  | { type: 'SUPPORT_UPDATED'; ticketId: string }
  /** Private reaction update delivered only to the DM's two participants. */
  | {
      type: 'DIRECT_MESSAGE_REACTION_UPDATED'
      gameId: string
      messageId: string
      fromUserId: string
      toUserId: string
      reactions: MessageReactionResponse[]
    }
  /**
   * Live presence snapshot for a game.
   *   online — full set of human users with at least one open websocket to
   *            this game's channel (same semantics as the legacy `userIds`
   *            field, kept for back-compat).
   *   live   — subset of online whose client is currently focused
   *            (AppState 'active' on mobile / visibilitychange visible on web).
   * Bots are always considered live and are NOT included in either array —
   * clients should union these sets with isBot=true players when rendering.
   * Invariant: live ⊆ online always.
   */
  | { type: 'PRESENCE_UPDATE'; online: string[]; live: string[] }
  /**
   * Lightweight intermediate-step broadcast emitted during the bot loop
   * between each atomic bot action (one claim, one attack roll, one fortify,
   * etc.). Carries only the raw game state JSON — NOT a full GameResponse —
   * so clients can render live troop counts without waiting for the end-of-turn
   * GAME_UPDATED. Clients that only care about final state can safely ignore
   * this event; the full GAME_UPDATED still fires at the end of every turn.
   *
   * Introduced in PR B of the perf-optimization sequence to eliminate the
   * expensive `prisma.game.update({ include: gameIncludeActive })` call on
   * each intermediate bot step. The final write retains the full include.
   */
  | { type: 'GAME_STATE_PATCH'; gameId: string; state: Record<string, unknown> }
  | { type: 'PONG' }
  | { type: 'ERROR'; message: string }

export type ClientEvent =
  | { type: 'SUBSCRIBE'; gameId: string }
  | { type: 'PING' }
  /** Client just became focused (AppState 'active' / visibilitychange visible). */
  | { type: 'FOCUS' }
  /** Client lost focus (AppState 'background'/'inactive' / visibilitychange hidden). */
  | { type: 'BLUR' }

// ─── Zod schemas ─────────────────────────────────────────────────────────────
// Contract-layer validation schemas. Import these in tests to assert that
// formatGame / wire payloads conform to the expected shape.
export { WireGameStateSchema, GameResponseSchema, ServerEventSchema, GameEventsResponseSchema } from './schemas.js'

// ─── Shared pure gameplay rules ───────────────────────────────────────────────
// Single source of truth for gameplay logic consumed by both the engine
// (apps/api/src/engine/) and the mobile client (apps/mobile/lib/).
// See packages/api-types/src/gameLogic.ts for the authoritative implementation.
export {
  reinforcementCount, isValidSet, scheduleBonus,
  borderReach, hasBorderTo, attackDiceSides, defendDiceSides, DEFAULT_DICE_SIDES,
  armyHeadroom, placeableArmies,
} from './gameLogic.js'
export type { BorderDice } from './gameLogic.js'
export type { BorderReachTerritory } from './gameLogic.js'
export type { ReinforcementTerritory, ReinforcementRegion } from './gameLogic.js'

export { formatDuration } from './duration.js'
export { resolveRuleSet, resolveRuleSets, ruleLinkPairs, ruleReachTargets, ruleMatchupPairs } from './ruleOverlay.js'
export type { RuleOverlay, RuleSet, RuleCard, RuleFact, RuleGlyph, DieSides, BoardChip, TerritorySelector, ResolvedRuleSet, RuleOverlayMap } from './ruleOverlay.js'

// ─── Dice odds and luck ──────────────────────────────────────────────────────
export { rollOutcomes } from './diceOdds.js'
export type { RollOutcome } from './diceOdds.js'
export {
  computeDiceLuck,
  diceLuckSnapshot,
  diceLuckPercentile,
  diceLuckVerdict,
  aggregateDiceLuckHistory,
  DICE_LUCK_PERCENTILE_MIN_ROLLS,
} from './diceLuck.js'
export type {
  DiceLuckHistory,
  DiceLuckHistoryGame,
  DiceLuckLeaderboardGame,
  DiceLuckLeaderboardOverall,
  DiceLuckLeaderboardPlayer,
  DiceLuckLeaderboardResponse,
  DiceLuckHistoryInput,
  DiceLuckResult,
  DiceLuckRollDetail,
  DiceLuckPlayerResult,
  DiceLuckPlayerSummary,
  DiceLuckSnapshot,
  DiceLuckVerdict,
} from './diceLuck.js'

// ─── Player status ───────────────────────────────────────────────────────────
export { isPlayerResigned, resignedSlotIds } from './playerStatus.js'
export type { PlayerStatusSource } from './playerStatus.js'

// ─── Lobby seat count ────────────────────────────────────────────────────────
export { PLAYER_SEATS_MIN, PLAYER_SEATS_MAX, PLAYER_SEATS_DEFAULT, mapPlayerCapacity, mapSeatRange } from './seats.js'

// ─── Teams ───────────────────────────────────────────────────────────────────
export { TEAM_IDS, TEAM_SIZES, TEAM_SHAPES, TEAM_BOTS_NOTE, teamsConfigOf, isTeamGame, teamForSlot, teamLabel, teamShape, teamFormats, validateTeamFormat, snapTeamPlayers, snapTeamSeats, teamOf, areAllies, isAlly, canPlaceOn, activeTeams, teamMembers, teamChatTeam, teamChatMembers } from './teams.js'
export type { TeamFormat, TeamFormatCheck, TeamFormatError, TeamShapeName, TeamsState, TeamChatSeat, TeamChatState } from './teams.js'
export { DEFAULT_GAME_CONFIG, gameConfigWithDefaults } from './gameConfig.js'
export { applyEventToMap, reconstructTerritoriesAt } from './replay.js'
export type { HistoricalTerritory } from './replay.js'
export { fogOfWarOf, isFogActive, withFogPlacement, fogSide, visibleTerritoryIds, redactWireStateForSide, redactAggressionForSide, redactEventsForSide, annotateFogVisibility, stripFogAnnotations } from './fog.js'
export type { FogMapTerritory } from './fog.js'
export { parseNews, newsPlainText, newsHtml, safeNewsUrl, newsAppRoute } from './newsFormat.js'
export type { NewsSpan, NewsBlock } from './newsFormat.js'
